import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Card } from '@fluentui/react-components'
import {
    AlbumFilled,
    AlbumRegular,
    bundleIcon,
    ClipboardTaskFilled,
    ClipboardTaskRegular,
    DocumentMultipleFilled,
    DocumentMultipleRegular,
    FlowchartFilled,
    FlowchartRegular,
    MoneyFilled,
    MoneyRegular,
} from '@fluentui/react-icons'
import { generatePath } from 'react-router'
import OverflowToolbarPanel, {
    HammockToolbarButtonData,
    HammockToolbarProps,
    useOverflowToolbarPanelStyles,
} from '../../../components/Overflow/OverflowToolbarPanel'
import { usePageStyles } from '../File/ViewFile'
import { crmRoutePrefix, ViewCRMProps } from './ViewCRM'
import useModules from '../../../hooks/module/useModules'
import useRoles, { Role } from '../../../hooks/useRoles'
import { ModuleType } from '../../../context/GlobalContext'

export enum CRMTabKey {
    File = 'file',
    Document = 'document',
    Task = 'task',
    Workflow = 'workflow',
    Finance = 'finance',
}

const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)
const ClipboardTaskIcon = bundleIcon(ClipboardTaskFilled, ClipboardTaskRegular)
const DocumentIcon = bundleIcon(DocumentMultipleFilled, DocumentMultipleRegular)
const FlowchartIcon = bundleIcon(FlowchartFilled, FlowchartRegular)
const MoneyIcon = bundleIcon(MoneyFilled, MoneyRegular)

export default function ViewCRMTabs(props: ViewCRMProps): ReactElement {
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const pageStyles = usePageStyles()
    const toolbarStyles = useOverflowToolbarPanelStyles()

    const activeButton = useMemo<CRMTabKey>(() => {
        return props.routeMatch.params?.tab || CRMTabKey.File
    }, [props.routeMatch])

    const onSelectButton = (value?: string) => {
        const button = buttons.find(b => b.value === value)
        if (button?.route) {
            history.push(generatePath(crmRoutePrefix + button.route, props.routeMatch.params))
        }
    }

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            className: pageStyles.toolbar,
        }),
        [],
    )

    const buttons = useMemo<HammockToolbarButtonData[]>(
        () => [
            {
                value: CRMTabKey.File,
                icon: <AlbumIcon />,
                titleKey: 'crm.tab.file',
                route: `/${CRMTabKey.File}`,
                onClick: onSelectButton,
            },
            {
                value: CRMTabKey.Document,
                icon: <DocumentIcon />,
                titleKey: 'crm.tab.document',
                route: `/${CRMTabKey.Document}`,
                onClick: onSelectButton,
            },
            {
                value: CRMTabKey.Task,
                icon: <ClipboardTaskIcon />,
                titleKey: 'crm.tab.task',
                route: `/${CRMTabKey.Task}`,
                filter: () => modules.has(ModuleType.Tasks),
                onClick: onSelectButton,
            },
            {
                value: CRMTabKey.Workflow,
                icon: <FlowchartIcon />,
                titleKey: 'crm.tab.workflow',
                route: `/${CRMTabKey.Workflow}`,
                filter: () => modules.has(ModuleType.Workflow),
                onClick: onSelectButton,
            },
            {
                value: CRMTabKey.Finance,
                icon: <MoneyIcon />,
                titleKey: 'crm.tab.finance',
                route: `/${CRMTabKey.Finance}`,
                filter: () => modules.any([ModuleType.Accounting, ModuleType.Invoices]) && roles.any([Role.Invoice]),
                onClick: onSelectButton,
            },
        ],
        [modules.loaded],
    )

    return (
        <Card className={pageStyles.toolbarCard}>
            <OverflowToolbarPanel
                containerClassName={pageStyles.toolbarTabs}
                toolbarProps={toolbarProps}
                getToolbarButtonProps={button => ({
                    appearance: activeButton === button?.value ? 'primary' : 'subtle',
                    disabled: button?.disabled,
                })}
                getMenuItemProps={button => ({
                    className: activeButton === button?.value && toolbarStyles.selectedMenuItem,
                    disabled: button?.disabled,
                })}
                buttons={buttons}
                overflowButtonLabelKey={'base.item.label'}
            />
        </Card>
    )
}
